// Shared domain types (mirroring the Go API DTOs) and presentation metadata.

export interface Member {
  id: string;
  name: string;
  initials: string;
  color: string;
}

export interface CurrentUser extends Member {
  email: string;
}

export interface Household {
  id: string;
  name: string;
}

export type EventCategory = "family" | "health" | "work" | "social" | "home";

export interface CalendarEvent {
  id: string;
  title: string;
  date: string; // YYYY-MM-DD
  time: string | null; // HH:MM or null for all-day
  allDay: boolean;
  who: string; // member id, or "both"
  category: EventCategory;
}

export type BillCategory = "utilities" | "rent" | "internet" | "subscriptions" | "insurance" | "other";

export interface Bill {
  id: string;
  name: string;
  amount: number;
  dueDate: string; // YYYY-MM-DD
  category: BillCategory;
  recurrence: "monthly" | "yearly";
  paid: boolean;
}

export interface ShoppingList {
  id: string;
  name: string;
}

export interface ShoppingItem {
  id: string;
  listId: string;
  name: string;
  qty: string | null;
  checked: boolean;
  addedBy: string | null;
}

export interface ExpenseTag {
  id: string;
  name: string;
  color: TagColor;
}

export interface Budget {
  id: string;
  name: string;
}

// ----- Presentation metadata ----------------------------------------------

/** Per-member color classes, keyed by the `color` assigned server-side. */
export const MEMBER_STYLES: Record<string, { avatar: string; dot: string; ring: string }> = {
  sky: { avatar: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300", dot: "bg-sky-500", ring: "ring-sky-500" },
  rose: { avatar: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300", dot: "bg-rose-500", ring: "ring-rose-500" },
  emerald: { avatar: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300", dot: "bg-emerald-500", ring: "ring-emerald-500" },
  amber: { avatar: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300", dot: "bg-amber-500", ring: "ring-amber-500" },
  violet: { avatar: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300", dot: "bg-violet-500", ring: "ring-violet-500" },
};

export function memberStyle(color: string | undefined) {
  return MEMBER_STYLES[color ?? "sky"] ?? MEMBER_STYLES.sky!;
}

export const EVENT_CATEGORIES: Record<EventCategory, { label: string; dot: string }> = {
  family: { label: "Family", dot: "bg-emerald-500" },
  health: { label: "Health", dot: "bg-rose-500" },
  work: { label: "Work", dot: "bg-sky-500" },
  social: { label: "Social", dot: "bg-violet-500" },
  home: { label: "Home", dot: "bg-amber-500" },
};

/**
 * Expense tag palette. Keys are stored in the database (see `validColors` in
 * internal/finance/finance.go — the two lists must stay in sync); Tailwind
 * cannot build class names from runtime values, so the classes are spelled out.
 */
export const TAG_COLORS = {
  sky: { chip: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300", dot: "bg-sky-500" },
  rose: { chip: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300", dot: "bg-rose-500" },
  emerald: { chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300", dot: "bg-emerald-500" },
  amber: { chip: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300", dot: "bg-amber-500" },
  violet: { chip: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300", dot: "bg-violet-500" },
  pink: { chip: "bg-pink-100 text-pink-800 dark:bg-pink-950 dark:text-pink-300", dot: "bg-pink-500" },
  teal: { chip: "bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300", dot: "bg-teal-500" },
  slate: { chip: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300", dot: "bg-slate-500" },
} as const;

export type TagColor = keyof typeof TAG_COLORS;

export const TAG_COLOR_KEYS = Object.keys(TAG_COLORS) as TagColor[];

export const DEFAULT_TAG_COLOR: TagColor = "sky";

export function tagStyle(color: string | undefined) {
  return TAG_COLORS[(color ?? DEFAULT_TAG_COLOR) as TagColor] ?? TAG_COLORS[DEFAULT_TAG_COLOR];
}

export const BILL_CATEGORIES: Record<BillCategory, { label: string; chip: string }> = {
  utilities: { label: "Utilities", chip: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300" },
  rent: { label: "Rent", chip: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300" },
  internet: { label: "Internet", chip: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300" },
  subscriptions: { label: "Subscriptions", chip: "bg-pink-100 text-pink-800 dark:bg-pink-950 dark:text-pink-300" },
  insurance: { label: "Insurance", chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" },
  other: { label: "Other", chip: "bg-muted text-muted-foreground" },
};
